import type { ErrorParams } from "@contract";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage, type UiErrorCode } from "../../../i18n/errors";
import { Button } from "../control/Button";
import { cx } from "../cx";
import { RetryIcon } from "../icons";

/** A failed panel: the translated error, its machine code in mono, and a retry. */
export function ErrorState({
  code,
  params,
  onRetry,
  busy = false,
  children,
  className = "",
}: {
  code: UiErrorCode;
  params?: ErrorParams;
  onRetry?: () => void;
  busy?: boolean;
  /** Extra context under the message, e.g. what was being attempted. */
  children?: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation(["components", "errors"]);
  return (
    <div
      role="alert"
      data-component="ErrorState"
      className={cx(
        "flex flex-col items-start gap-2 rounded-md border border-danger-line bg-danger-soft px-5 py-4",
        className,
      )}
    >
      <p className="text-ui font-medium text-danger">{t("components:error.title")}</p>
      {/* errorMessage, not a bare t(): it maps the code's own param onto
          i18next's `count` so a plural entry reads as a sentence. */}
      <p className="text-ui text-ink">{errorMessage(t, code, params)}</p>
      {children ? <p className="text-ui text-ink-2">{children}</p> : null}
      <p className="label-mono">
        {t("components:error.code")} · {code}
      </p>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} busy={busy} className="mt-1">
          <RetryIcon size={14} />
          {t("components:error.retry")}
        </Button>
      ) : null}
    </div>
  );
}
